import Timer from "components/Timer";
import { Dispatch, SetStateAction, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
    addStateSupplyAlertProfile,
    editStateSupplyAlertProfile,
    supplyAlertProfileFetching,
    rowEditSupplyAlertProfile,
    savingSupplyAlertProfile,
} from "../atoms/atoms";
import { ISupplyAlertProfile } from "../types";
import useAddSupplyAlertProfile from "../hooks/useAddSupplyAlertProfile";
import useEditSupplyAlertProfile from "../hooks/useEditSupplyAlertProfile";
import ConfirmBoxModal from "components/ConfirmBoxModal";
import SubmitHookForm from "components/SubmitHookForm";
import CancelButton from "components/CancelButton";
import SupplyAlertProfileDetails from "./SupplyAlertProfileDetails";

export function SupplyAlertProfileForm(props: { reFetch: () => void, setTableFilteredData: Dispatch<SetStateAction<ISupplyAlertProfile[]>> }) {
    // Drilled Props
    const { reFetch, setTableFilteredData } = props;

    // Atom States
    const [isAddState, setIsAddState] = useRecoilState(addStateSupplyAlertProfile);
    const [actionEditState, setActionEditState] = useRecoilState(editStateSupplyAlertProfile);
    const [isSaving, setIsSaving] = useRecoilState(savingSupplyAlertProfile);
    const [editData, setEditData] = useRecoilState(rowEditSupplyAlertProfile);
    const setIsFetching = useSetRecoilState(supplyAlertProfileFetching);

    // Local States
    const [newData, setNewData] = useState<ISupplyAlertProfile>();
    const [saveModal, setSaveModal] = useState(false);
    const [changes, setChanges] = useState(false);

    // Hook Functions
    const updateData = useEditSupplyAlertProfile();
    const addData = useAddSupplyAlertProfile();

    // Form Functions
    const useFormMethods = useForm({
        defaultValues: { ...editData },
    });

    const onSubmit = (data) => {
        const value = {
            ...data,
            supplyAlertProfileName: data.supplyAlertProfileName.trim(),
        };
        setNewData(value);
        setSaveModal(true);
        setChanges(false);
        setIsSaving(true);
    };

    const handleCancel = () => {
        useFormMethods.reset();
        useFormMethods.clearErrors();
        setEditData(undefined);
        setIsAddState(false);
        setActionEditState(false);
    };

    const handleSave = async () => {
        if (newData) {
            if (actionEditState) {
                updateData(newData);
                setTableFilteredData((current) =>
                    current.map((data) => {
                        if (data.supplyAlertProfileId === newData.supplyAlertProfileId) {
                            return {
                                ...data,
                                isActive: newData.isActive,
                                supplyAlertProfileName: newData.supplyAlertProfileName,
                            };
                        }
                        return data;
                    })
                );
            } else {
                addData(newData);
            }
        }

        await Timer(100);
        setIsFetching(true);
        setIsSaving(false);
        useFormMethods.reset();
        useFormMethods.clearErrors();
        setEditData(undefined);
        setIsAddState(false);
        setActionEditState(false);
        reFetch();
    };

    return (
        <>
            <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
                <FormProvider {...useFormMethods}>
                    <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
                        <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
                            <div className="ml-2 font-bold text-primary text-base">
                                {isAddState ? "Add New Supply Alert Profile" : "Edit Supply Alert Profile"}
                            </div>
                            <div className="flex justify-between gap-2 mr-2">
                                <SubmitHookForm
                                    type="submit"
                                    disabled={!changes}
                                    buttonText={isSaving ? "Saving.." : "Save"}
                                    className={undefined}
                                    onClick={undefined}
                                />
                                <CancelButton
                                    onClick={(event) => {
                                        event.preventDefault();
                                        handleCancel();
                                    }}
                                />
                            </div>
                        </div>
                        <div className="mx-2 border-b-2 border-secondary"></div>
                        <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
                            <b> Note:</b> All fields with * are required to fill.
                        </div>
                    </form>
                    <div>
                        <SupplyAlertProfileDetails data={editData} setChanges={setChanges} />
                    </div>
                </FormProvider>
            </div>

            {saveModal && (
                <ConfirmBoxModal
                    ModalTitle={isAddState ? "Add Supply Alert Profile" : "Update Supply Alert Profile"}
                    qConfirmation={`Are you sure you want to ${isAddState ? "add " : "update "
                        } this?`}
                    isOpen={saveModal}
                    onClose={() => {
                        setSaveModal(false);
                        setIsSaving(false);
                        setChanges(true);
                    }}
                    onClickFunction={() => {
                        setSaveModal(false);
                        handleSave();
                    }}
                    cancelButtonRef={undefined}
                />
            )}
        </>
    );
}

export default SupplyAlertProfileForm;
